import React, { useEffect, useState } from 'react';
import cn from "classnames";
import styles from "./ContractRemuneration.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import DualButton from "../../../components/DualButton";
import Dropdown from "../../../components/Dropdown";

import { checkSalary } from "../../../services/salary.service";

const ContractRemuneration = ({ className, data, updateData, validationErrors = {} }) => {
  // Récupérer les données du contrat
  const remunerationData = data?.contract?.remuneration || {};
  const studentData = data?.student || {};
  const contractDates = data?.contract?.datesDerogations || {};

  // États pour les champs de rémunération
  const [salaryYear1, setSalaryYear1] = useState(remunerationData?.salary_year_1 || '');
  const [hasConventionSalary, setHasConventionSalary] = useState(remunerationData?.has_convention_salary || 'Non');
  const [conventionSalary, setConventionSalary] = useState(remunerationData?.convention_salary || '');
  const [smicPercentage, setSmicPercentage] = useState(remunerationData?.smic_percentage || '');

  // États pour le calcul du salaire
  const [salaryPeriods, setSalaryPeriods] = useState([]);
  const [detailedSalaryPeriods, setDetailedSalaryPeriods] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const [showSalaryDetails, setShowSalaryDetails] = useState(false);
  const [dataChecked, setDataChecked] = useState(false); // Pour éviter des appels répétés
  const [missingDataInfo, setMissingDataInfo] = useState('');

  // Gestionnaire pour les changements de rémunération
  const handleRemunerationChange = (e) => {
    const { name, value } = e.target;
    
    // Mapper salary_year_1 vers hiring_salary pour PocketBase
    const fieldName = name === 'salary_year_1' ? 'hiring_salary' : name;

    if (updateData) {
      updateData('remuneration', { ...data.contract?.remuneration, [fieldName]: value });
    }
  };

  // Gestionnaire pour les DualButton
  const handleDualButtonChange = (name, value) => {
    if (updateData) {
      updateData('remuneration', { ...data.contract?.remuneration, [name]: value });
    }
  };

  // Effet pour initialiser les données de rémunération
  useEffect(() => {
    if (data?.contract?.remuneration) {
      const remuneration = data.contract.remuneration;
      // Utiliser hiring_salary au lieu de salary_year_1
      setSalaryYear1(remuneration.hiring_salary || remuneration.salary_year_1 || '');
      setHasConventionSalary(remuneration.has_convention_salary || 'Non');
      setConventionSalary(remuneration.convention_salary || '');
      setSmicPercentage(remuneration.smic_percentage || '');
    }
  }, [data?.contract?.remuneration]);

  // Effet pour réinitialiser les vérifications lorsque les données changent
  useEffect(() => {
    // Réinitialiser les vérifications si les données changent
    setDataChecked(false);
    setMissingDataInfo('');
    setCalculationError('');

    // Log de débogage pour vérifier les données reçues
    console.log('ContractRemuneration - Données reçues:', {
      'Date de naissance': studentData?.birth_date,
      'Date début contrat': contractDates?.start_date_of_contract,
      'Date fin contrat': contractDates?.end_date_of_contract
    });
  }, [studentData, contractDates]);

  // Effet pour calculer automatiquement le salaire au chargement du composant
  useEffect(() => {
    // Éviter de recalculer si déjà fait ou si calcul en cours
    if (dataChecked || isCalculating) return;

    // Vérifier que toutes les données nécessaires sont disponibles
    if (studentData?.birth_date &&
      contractDates?.start_date_of_contract &&
      contractDates?.end_date_of_contract) {
      // Marquer comme vérifié pour éviter des appels répétés
      setDataChecked(true);
      // Appeler la fonction de calcul du salaire
      calculateSalary();
    } else {
      // Identifier les données manquantes
      let missingData = [];
      if (!studentData?.birth_date) missingData.push("date de naissance de l'étudiant");
      if (!contractDates?.start_date_of_contract) missingData.push("date de début de contrat");
      if (!contractDates?.end_date_of_contract) missingData.push("date de fin de contrat");

      if (missingData.length > 0) {
        setMissingDataInfo(`Impossible de calculer automatiquement le salaire. Données manquantes : ${missingData.join(', ')}.`);
      }

      setDataChecked(true); // Marquer comme vérifié pour ne pas répéter le message
    }
    // Retirer salaryPeriods.length des dépendances pour éviter la boucle infinie
  }, [studentData?.birth_date, contractDates?.start_date_of_contract, contractDates?.end_date_of_contract, dataChecked, isCalculating]);

  // Fonction pour construire le tableau annual_remuneration
  const buildAnnualRemunerationArray = (detailedPeriods) => {
    // Grouper les périodes par année pour calculer correctement les parties
    const periodsByYear = {};
    
    detailedPeriods.forEach(period => {
      if (!periodsByYear[period.annee]) {
        periodsByYear[period.annee] = [];
      }
      periodsByYear[period.annee].push(period);
    });
    
    // Construire le tableau avec les ordres corrects
    return detailedPeriods.map(period => {
      // Convertir les dates au format ISO
      const dateDebut = new Date(period.startDate).toISOString();
      const dateFin = new Date(period.endDate).toISOString();
      
      // Calculer la partie dans l'année (1, 2, 3...)
      const yearPeriods = periodsByYear[period.annee];
      const partieIndex = yearPeriods.findIndex(p => 
        p.startDate === period.startDate && p.endDate === period.endDate
      ) + 1;
      
      // Calculer l'ordre (année.partie)
      const ordre = `${period.annee}.${partieIndex}`;
      
      return {
        dateDebut,
        dateFin,
        taux: parseInt(period.percentage),
        typeSalaire: period.smicType || 'SMIC',
        ordre
      };
    });
  };

  // Calcul du pourcentage du SMIC lorsque le salaire change
  useEffect(() => {
    // Valeur approximative du SMIC mensuel brut (à mettre à jour selon la valeur actuelle)
    const smicValue = 1766.92; // SMIC 2024

    if (salaryYear1 && salaryYear1 > 0) {
      const percentage = ((parseFloat(salaryYear1) / smicValue) * 100).toFixed(2);

      // Ne mettre à jour que si la valeur a changé
      if (percentage !== smicPercentage) {
        setSmicPercentage(percentage);

        if (updateData) {
          updateData('remuneration', {
            ...data.contract?.remuneration,
            hiring_salary: salaryYear1,
            smic_percentage: percentage
          });
        }
      }
    }
  }, [salaryYear1]);

  // Fonction pour calculer le salaire en fonction de l'âge et des dates de contrat
  const calculateSalary = async () => {
    // Si déjà en cours de calcul, ne pas relancer
    if (isCalculating) return;
    // Vérifier que toutes les données nécessaires sont présentes
    if (!studentData.birth_date) {
      setCalculationError("La date de naissance de l'étudiant est requise");
      return;
    }

    if (!contractDates.start_date_of_contract || !contractDates.end_date_of_contract) {
      setCalculationError("Les dates de début et de fin de contrat sont requises");
      return;
    }

    setIsCalculating(true);
    setCalculationError('');

    try {
      const result = await checkSalary({
        birthDate: studentData.birth_date,
        startDate: contractDates.start_date_of_contract,
        endDate: contractDates.end_date_of_contract
      });

      if (result.success) {
        // Accéder aux données via result.data
        setSalaryPeriods(result.data?.periodes || []);
        setDetailedSalaryPeriods(result.data?.periodesDetaillees || []);
        setShowSalaryDetails(true);

        // Si nous avons des périodes, utiliser le pourcentage de la première période
        // pour mettre à jour le salaire initial et le pourcentage du SMIC
        if (result.data?.periodes && result.data.periodes.length > 0) {
          const firstPeriod = result.data.periodes[0];
          const initialSalary = firstPeriod.salary;
          const initialPercentage = firstPeriod.percentage;

          setSalaryYear1(initialSalary.toString());
          setSmicPercentage(initialPercentage.toString());

          // Construire le tableau annual_remuneration
          const annualRemuneration = buildAnnualRemunerationArray(result.data.periodesDetaillees || []);

          if (updateData) {
            updateData('remuneration', {
              ...data.contract?.remuneration,
              hiring_salary: initialSalary.toString(),
              smic_percentage: initialPercentage.toString(),
              annual_remuneration: JSON.stringify(annualRemuneration)
            });
          }
        }
      } else {
        setCalculationError(result.message || "Erreur lors du calcul du salaire");
      }
    } catch (error) {
      console.error("Erreur lors du calcul du salaire:", error);
      setCalculationError("Une erreur est survenue lors du calcul du salaire");
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <>
      <Card className={cn(styles.card, className)} title="Rémunération" classTitle="title-blue">
        <div className={styles.field}>
          <DualButton
            label="Existe t'il un salaire minimum conventionné (SMC) ? "
            option1="Oui"
            option2="Non"
            value={hasConventionSalary}
            onChange={(value) => {
              setHasConventionSalary(value);
              handleDualButtonChange('has_convention_salary', value);
            }}
            multiSelect={false}
          />
        </div>

        {hasConventionSalary === 'Oui' && (
          <TextInput
            className={styles.field}
            label="Salaire minimum conventionné"
            name="convention_salary"
            type="number"
            placeholder="Ex: 1000"
            value={conventionSalary}
            onChange={(e) => {
              setConventionSalary(e.target.value);
              handleRemunerationChange(e);
            }}
            required
            error={validationErrors.convention_salary}
          />
        )}
      </Card>

      <Card className={cn(styles.card, className)} title="Salaire brut mensuel à l'embauche" classTitle="title-blue">
        <div className={styles.group}>
          <TextInput
            className={styles.field}
            label="Salaire brut de l'apprenti à l'embauche (€)"
            name="salary_year_1"
            type="number"
            placeholder="Ex: 1000"
            value={salaryYear1}
            onChange={(e) => {
              setSalaryYear1(e.target.value);
              handleRemunerationChange(e);
            }}
            required
            error={validationErrors.salary_year_1}
          />

          <TextInput
            className={styles.field}
            label="% du SMIC"
            name="smic_percentage"
            type="number"
            placeholder="Ex: 50"
            value={smicPercentage}
            onChange={(e) => {
              setSmicPercentage(e.target.value);
              handleRemunerationChange(e);
            }}
            readOnly
            error={validationErrors.smic_percentage}
          />
        </div>

        {calculationError && (
          <div className={styles.errorMessage}>{calculationError}</div>
        )}

        {missingDataInfo && !calculationError && (
          <div className={styles.infoMessage}>{missingDataInfo}</div>
        )}

        {isCalculating && (
          <div className={styles.infoMessage}>Calcul automatique en cours...</div>
        )}
      </Card>

      {showSalaryDetails && detailedSalaryPeriods.length > 0 && (
        <Card className={cn(styles.card, className)} title="Tableau de rémunération" classTitle="title-blue">
          {detailedSalaryPeriods.map((period, index) => {
            const anneeKey = `annee_${period.annee}`;
            return (
              <div key={index} className={styles.yearSection}>
                <h3 className={styles.yearTitle}>Année {period.annee}</h3>

                <div className={styles.group}>
                  <div className={styles.field}>
                    <TextInput
                      label="Date de début"
                      type="date"
                      value={period.startDate}
                      readOnly
                    />
                  </div>

                  <div className={styles.field}>
                    <TextInput
                      label="Date de fin"
                      type="date"
                      value={period.endDate}
                      readOnly
                    />
                  </div>

                  <div className={styles.field}>
                    <TextInput
                      label="Pourcentage"
                      name={`${anneeKey}_percentage`}
                      type="number"
                      value={period.percentage}
                      onChange={(e) => {
                        // Mettre à jour le pourcentage et recalculer le salaire
                        const newPercentage = e.target.value;
                        const newSalary = ((parseFloat(newPercentage) / 100) * 1766.92).toFixed(2);

                        // Mettre à jour les données dans le state
                        const updatedPeriods = [...detailedSalaryPeriods];
                        updatedPeriods[index] = {
                          ...period,
                          percentage: newPercentage,
                          salary: newSalary,
                          smicType: period.smicType || 'SMIC'
                        };
                        setDetailedSalaryPeriods(updatedPeriods);

                        // Si c'est la première période, mettre à jour aussi le salaire initial
                        if (index === 0) {
                          setSalaryYear1(newSalary);
                          setSmicPercentage(newPercentage);
                        }

                        // Reconstruire et sauvegarder le tableau annual_remuneration
                        const annualRemuneration = buildAnnualRemunerationArray(updatedPeriods);
                        
                        if (updateData) {
                          updateData('remuneration', {
                            ...data.contract?.remuneration,
                            hiring_salary: index === 0 ? newSalary : data.contract?.remuneration?.hiring_salary,
                            smic_percentage: index === 0 ? newPercentage : data.contract?.remuneration?.smic_percentage,
                            annual_remuneration: JSON.stringify(annualRemuneration)
                          });
                        }
                      }}
                      suffix="%"
                    />
                  </div>

                  <div className={styles.field}>
                    <Dropdown
                      label="SMIC ou SMC"
                      value={period.smicType || 'SMIC'}
                      options={['SMIC', 'SMC']}
                      setValue={(value) => {
                        // Mettre à jour le type de salaire (SMIC ou SMC)
                        const updatedPeriods = [...detailedSalaryPeriods];
                        updatedPeriods[index] = {
                          ...period,
                          smicType: value
                        };
                        setDetailedSalaryPeriods(updatedPeriods);

                        // Reconstruire et sauvegarder le tableau annual_remuneration
                        const annualRemuneration = buildAnnualRemunerationArray(updatedPeriods);
                        
                        if (updateData) {
                          updateData('remuneration', {
                            ...data.contract?.remuneration,
                            annual_remuneration: JSON.stringify(annualRemuneration)
                          });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {showSalaryDetails && salaryPeriods.length > 0 && (
        <Card className={cn(styles.card, className)} title="Estimation de l'évolution du salaire" classTitle="title-blue">
          <div className={styles.salaryEvolution}>
            <table className={styles.salaryTable}>
              <thead>
                <tr>
                  <th>Année</th>
                  <th>Période</th>
                  <th>Âge</th>
                  <th>% du SMIC</th>
                  <th>Salaire brut</th>
                </tr>
              </thead>
              <tbody>
                {detailedSalaryPeriods.map((period, index) => (
                  <tr key={index}>
                    <td>{period.annee}</td>
                    <td>{period.startDate} au {period.endDate}</td>
                    <td>{period.age} ans</td>
                    <td>{period.percentage}%</td>
                    <td>{period.salary} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
};

export default ContractRemuneration;
